import { Component, HostListener, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PendoService } from 'analytics-lib';
import { BullhornNamespace, InveniasNamespace, SalesforceNamespace } from 'datasource-service-lib';
import { LocalFileNamespace } from 'datasource-service-lib';
import { IAttachmentMergeFile, IDataSourcePathCommand } from 'interfaces-lib';
import { UtilsService } from 'utils-service-lib';
import { runtimeEnvironment } from '../../../../environments/environment';
import { ISimpleTableRow } from '../../../common-components/simple-table/simple-table.interfaces';
import { IFileIdAndOwner } from '../../../interfaces/ifiletopathconverter';
import { ServiceProviderService } from '../../../services/service.provider.service';
import { buildSalesforceContactDocumentParameters } from '../../../utils/salesforce-contact-document-helpers';
import { DialogResult } from '../dialog-result';
import {
  DatasourceAttachFileComponent,
  IAttachFileDialogDataMultiEntity,
  IAttachFileDialogDataSingleEntity,
} from './datasource/datasource-attach-file.component';
import { LocalAttachFileComponent } from './local/local-attach-file.component';

@Component({
  selector: 'app-attach-file-dialog',
  templateUrl: './attach-file-dialog.component.html',
  styleUrls: [
    '../selection-dialogs.component.scss',
    '../selection-dialogs.chrome-extension.component.scss',
    'attach-file-dialog.component.scss',
  ],
  standalone: false,
})
export class AttachFileDialogComponent implements OnInit, OnDestroy {
  @ViewChildren(DatasourceAttachFileComponent)
  datasourceAttachFileList: QueryList<DatasourceAttachFileComponent>;

  @ViewChild(LocalAttachFileComponent)
  localAttachFile: LocalAttachFileComponent;

  renderMode = runtimeEnvironment.renderMode;

  files: ISimpleTableRow[] = [];
  fileSelected: IAttachmentMergeFile;
  multiDialogData: IAttachFileDialogDataSingleEntity[];
  selectedEntityIndex: number;

  constructor(
    private utilsService: UtilsService,
    private serviceProviderService: ServiceProviderService,
    public dialogRef: MatDialogRef<AttachFileDialogComponent>,
    private pendoService: PendoService,
    @Inject(MAT_DIALOG_DATA) public data: IAttachFileDialogDataMultiEntity,
  ) {
    dialogRef.disableClose = true;
    this.multiDialogData = data.entities.map((entity) => ({
      ...data,
      entity: entity,
    }));
  }

  static getDefaultMatConfig(): any {
    return {
      maxHeight: runtimeEnvironment.renderMode === 'web-app' ? '93vh' : '',
      maxWidth: runtimeEnvironment.renderMode === 'web-app' ? '88vw' : '100vw',
      minWidth: runtimeEnvironment.renderMode === 'web-app' ? '808px' : '',
      panelClass: [
        runtimeEnvironment.renderMode === 'web-app' ? '' : ['chrome-ext-full-view'],
        runtimeEnvironment.origin.namespace + '-namespace',
      ],
      height: 'auto',
      autoFocus: true,
    };
  }

  // Keyboard Shortcuts
  // - Use Escape to close the dialog
  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (!this.utilsService.escapableElementActive()) {
      if (event.code === 'Escape') {
        this.dialogRef.close();
        this.pendoService?.recordEvent('Escape key pressed - to close attach file dialog');
      }
    } else {
      this.utilsService.escapableElementActive(false);
    }
  }

  ngOnInit(): void {
    this.utilsService.dialogOpen(true);
  }

  onCancelClick(): void {
    this.dialogRef.close(DialogResult.CANCELLED);
  }

  onSkipClick() {
    if (this.fileSelected) {
      this.onOkClick();
    } else {
      this.dialogRef.close(new DialogResult(false, []));
    }
  }

  onDatasourceFileSelected(file: IAttachmentMergeFile, index: number): void {
    this.fileSelected = file;
    this.selectedEntityIndex = index;
    this.datasourceAttachFileList.filter((component, i) => i !== index).forEach((component) => component.clearFileSelection());
    this.localAttachFile?.clearFileSelection();
  }

  onLocalFileSelected(file: IAttachmentMergeFile): void {
    this.fileSelected = file;
    this.datasourceAttachFileList.forEach((component) => component.clearFileSelection());
  }

  get isDatasourceComponentVisible(): boolean {
    const source = this.data?.sectionConfig?.attachmentUploadSource;
    return (
      source === undefined ||
      BullhornNamespace.name === source ||
      SalesforceNamespace.name === source ||
      'both' === source ||
      this.data.templateNamespace === InveniasNamespace.name
    );
  }

  get isLocalComponentVisible(): boolean {
    const source = this.data?.sectionConfig?.attachmentUploadSource;
    return LocalFileNamespace.name === source || 'both' === source;
  }

  onOkClick(): void {
    const belongsTo = this.fileSelected?.belongsTo ?? {
      id: this.data.entities[this.selectedEntityIndex].id,
      type: this.data.entities[this.selectedEntityIndex].type,
    };
    const fileAndOwner: IFileIdAndOwner = {
      id: this.fileSelected.id,
      path: this.fileSelected.path,
      belongsTo: belongsTo,
    };

    const namespace = this.fileSelected.namespace ?? this.data?.templateNamespace;
    const dataSourcePathCommand: IDataSourcePathCommand = {
      namespace: namespace,
      path: this.serviceProviderService.getFileToPathConverter(namespace).convert(fileAndOwner),
    };

    const parameters = buildSalesforceContactDocumentParameters(namespace, this.fileSelected);
    if (parameters) {
      dataSourcePathCommand.parameters = parameters;
    }

    this.dialogRef.close(new DialogResult(false, [dataSourcePathCommand]));
  }

  ngOnDestroy(): void {
    this.utilsService.dialogOpen(false);
  }
}
